/**
 * Test health and triage (tiered-test-authoring §5, task 5.1's dashboard).
 *
 * Named views, not one ranked list — "flaky" and "always passing" answer
 * different questions, and a health screen that collapsed them into one
 * score would hide exactly the distinction task 1.8/§5 insists on keeping.
 *
 * REBUILT: the previous version showed a bare `Tier 1/2/3` pill per row with
 * no explanation of what that meant, a flat list with no way to see WHY a
 * test was flagged (no error message, no link to the run), and four
 * always-expanded sections that made "how many groups are there" and "what's
 * actually in each one" hard to tell apart at a glance. This version names
 * the origin plainly, is a real table, and every row expands to the actual
 * evidence — the same thing Reports already does for a run's own results.
 */
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type HealthDashboard, type TestHealthEntry } from '../api';

/** What a person should see instead of this project's own internal "tier" bookkeeping term. */
function kindLabel(e: TestHealthEntry): string {
  if (e.origin === 'tier1') return 'Client recording';
  if (e.tier === 2) return 'QA recipe';
  return 'Regression test';
}

/** Secondary detail for "Regression test" — hand-authored in the console, or a real spec file. */
function kindDetail(e: TestHealthEntry): string | null {
  if (e.origin === 'tier1' || e.tier === 2) return null;
  return e.origin === 'code' ? 'from a spec file' : 'authored in the console';
}

function fmtWhen(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export function HealthScreen() {
  const [dashboard, setDashboard] = useState<HealthDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.health().then(setDashboard).catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  if (error) return <div className="surface p-6 text-sm" style={{ color: 'hsl(var(--fail))' }}>{error}</div>;
  if (!dashboard) {
    return (
      <div className="p-8 text-center text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
        <p>Loading test health…</p>
        {/* This computes real ownership (git blame/log) for every test in the
            inventory — on a large suite that is a genuine minute-scale wait,
            not a stuck page. Said explicitly so it doesn't read as broken. */}
        <p className="mt-1 text-xs">
          This checks git history for every test, one at a time — on a large suite this can take up to a minute.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-lg font-semibold">Test health and triage</h1>
        <p className="mt-1 text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
          Computed from real run history across every way a test can be created — a client
          recording, a QA recipe, or a regression test. Nothing here predicts what a future
          change will break (that would need parsing a diff, which this deliberately does not
          do) — it only reports what runs have actually shown so far. Click a row to see why it
          is flagged.
        </p>
      </div>

      <Accordion
        title="High-value but failing"
        explain="Tests covering auth, core flows or payment, where more than half of the last 30 days' runs failed. These are worth looking at first."
        entries={dashboard.highValueButFailing}
        testid="health-high-value-failing"
        tone="fail"
        defaultOpen
      />
      <Accordion
        title="Flaky"
        explain="Tests whose result flips between pass and fail from one run to the next, rather than consistently doing one or the other. A flaky test erodes trust in every result it reports."
        entries={dashboard.flaky}
        testid="health-flaky"
        tone="warn"
      />
      <Accordion
        title="Not run in 30 days"
        explain="Tests with no run at all in the last 30 days — nobody has recently checked whether these still pass."
        entries={dashboard.staleOver30Days}
        testid="health-stale"
        tone="warn"
      />
      <Accordion
        title="Always passing"
        explain="Every run in the last 30 days passed. Shown for completeness, not as something to act on."
        entries={dashboard.alwaysPassing}
        testid="health-always-passing"
        tone="pass"
      />
    </div>
  );
}

function Accordion({
  title, explain, entries, testid, tone, defaultOpen,
}: {
  title: string;
  explain: string;
  entries: TestHealthEntry[];
  testid: string;
  tone: 'fail' | 'warn' | 'pass';
  defaultOpen?: boolean;
}) {
  return (
    <details className="surface overflow-hidden" data-testid={testid} open={defaultOpen}>
      <summary
        className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm font-semibold"
        style={{ listStyle: 'none' }}
      >
        <span className={`pill pill-${tone}`}>{entries.length}</span>
        <span>{title}</span>
        <span className="ml-2 text-xs font-normal" style={{ color: 'hsl(var(--text-faint))' }}>{explain}</span>
      </summary>
      <div className="border-t" style={{ borderColor: 'hsl(var(--border))' }}>
        {entries.length === 0 ? (
          <p className="px-4 py-3 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>None right now.</p>
        ) : (
          <HealthTable entries={entries} />
        )}
      </div>
    </details>
  );
}

function HealthTable({ entries }: { entries: TestHealthEntry[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            {['', 'Name', 'Kind', 'Application', 'Pass rate (30d)', 'Last run', 'Owner'].map((h) => (
              <th key={h} className="px-3 py-2 text-xs font-medium uppercase tracking-wide" style={{ color: 'hsl(var(--text-faint))' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const isOpen = expanded === e.testId;
            return (
              <Fragment key={e.testId}>
                <tr
                  onClick={() => setExpanded(isOpen ? null : e.testId)}
                  className="cursor-pointer transition hover:opacity-80"
                  style={{ borderBottom: isOpen ? undefined : '1px solid hsl(var(--border))' }}
                  data-testid={`health-row-${e.testId}`}
                >
                  <td className="px-3 py-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{isOpen ? '▾' : '▸'}</td>
                  <td className="px-3 py-2 font-medium">{e.name}</td>
                  <td className="px-3 py-2">
                    <span className="pill pill-info text-[11px]">{kindLabel(e)}</span>
                    {kindDetail(e) && (
                      <span className="ml-1.5 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{kindDetail(e)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2" style={{ color: 'hsl(var(--text-muted))' }}>{e.application}</td>
                  <td className="px-3 py-2">
                    {e.health.passRate30d === null ? (
                      <span style={{ color: 'hsl(var(--text-faint))' }}>no runs in 30d</span>
                    ) : (
                      <span style={{ color: e.health.passRate30d < 0.5 ? 'hsl(var(--fail))' : 'hsl(var(--text))' }}>
                        {Math.round(e.health.passRate30d * 100)}% ({e.health.runCount} run{e.health.runCount === 1 ? '' : 's'})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2" style={{ color: 'hsl(var(--text-muted))' }}>
                    {e.lastRun ? (
                      <span style={{ color: e.lastRun.status === 'failed' ? 'hsl(var(--fail))' : undefined }}>
                        {e.lastRun.status} · {fmtWhen(e.lastRun.at)}
                      </span>
                    ) : 'never run'}
                  </td>
                  <td className="px-3 py-2" style={{ color: 'hsl(var(--text-muted))' }}>
                    {e.ownership.who ?? <span style={{ color: 'hsl(var(--text-faint))' }}>unknown</span>}
                  </td>
                </tr>
                {isOpen && (
                  <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                    <td colSpan={7} className="px-3 py-3" style={{ background: 'hsl(var(--bg-overlay))' }}>
                      <ExpandedDetail entry={e} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The debug detail a triage row needs — same spirit as a run's own report: name
 *  what actually happened, not just that something is wrong. */
function ExpandedDetail({ entry: e }: { entry: TestHealthEntry }) {
  return (
    <div className="grid gap-4 text-sm sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <DetailRow label="Where it came from" value={`${kindLabel(e)}${kindDetail(e) ? ` — ${kindDetail(e)}` : ''}`} />
        {e.file && <DetailRow label="File" value={e.line ? `${e.file}:${e.line}` : e.file} mono />}
        <DetailRow label="Application" value={e.application} />
        <DetailRow label="Scheduled" value={e.scheduledCadence === 'never' ? 'on-demand only — never scheduled' : e.scheduledCadence} />
        <DetailRow
          label="Owner"
          value={e.ownership.who ?? `Unknown${e.ownership.detail ? ` — ${e.ownership.detail}` : ''}`}
        />
        <DetailRow label="Average duration" value={e.health.avgDurationMs === null ? '—' : `${(e.health.avgDurationMs / 1000).toFixed(1)}s`} />
        <DetailRow label="Flakiness" value={e.health.flakinessScore === null ? '—' : `${e.health.flakinessScore.toFixed(2)} (0 = never flips, 1 = flips every run)`} />
      </div>

      <div className="flex flex-col gap-1.5">
        {e.lastFailure ? (
          <div className="rounded-lg border p-3" style={{ borderColor: 'hsl(var(--fail) / 0.4)', background: 'hsl(var(--fail) / 0.06)' }}>
            <p className="text-xs font-semibold" style={{ color: 'hsl(var(--fail))' }}>
              Last failure — {fmtWhen(e.lastFailure.at)}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-xs" style={{ color: 'hsl(var(--text))' }}>
              {e.lastFailure.error ?? 'The run failed, but no error message was recorded for it.'}
            </p>
            {e.lastFailure.runId && (
              <Link to={`/runs/${e.lastFailure.runId}`} className="mt-2 inline-block text-xs underline" style={{ color: 'hsl(var(--accent))' }}>
                Open that run →
              </Link>
            )}
          </div>
        ) : (
          <div className="rounded-lg border p-3" style={{ borderColor: 'hsl(var(--border))' }}>
            <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>No recorded failure for this test.</p>
          </div>
        )}

        {e.lastRun && e.lastRun.runId && e.lastRun.runId !== e.lastFailure?.runId && (
          <Link to={`/runs/${e.lastRun.runId}`} className="text-xs underline" style={{ color: 'hsl(var(--accent))' }}>
            Open its most recent run ({e.lastRun.status}, {fmtWhen(e.lastRun.at)}) →
          </Link>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <p className="text-xs">
      <span style={{ color: 'hsl(var(--text-faint))' }}>{label}: </span>
      <span className={mono ? 'font-mono' : undefined}>{value}</span>
    </p>
  );
}
